import type { AppLanguage } from "@ibada/shared-types/i18n"
import { Currency } from "@ibada/shared-types/finance"

import { http } from "@/shared/lib/api/http"
import {
  type AcceptWorkspaceInvitationRequest,
  type AcceptWorkspaceInvitationResponse,
  type AuthMeResponse,
  type TenantBootstrapResponse,
  AuthMeWorkspaceState,
  UserBranchAccessStatus,
  type CreateBranchRequest,
  type CreateBranchResponse,
  type DeclineWorkspaceInvitationRequest,
  type DeclineWorkspaceInvitationResponse,
  DepartmentEntryMode,
  type InvitationPreviewResponse,
  type InvitationRecord,
  type MarkWorkspaceInvitationsViewedRequest,
  type MarkWorkspaceInvitationsViewedResponse,
  type ResolveWorkspaceInvitationResponse,
  type SetDefaultWorkspaceRequest,
  type TenantAccessResolveResponse,
  type WorkspaceSummary,
  WorkspaceStructureType,
} from "@/features/auth/model/auth-types"

/**
 * Bootstrap department payloads arrive snake_cased (ADR-0004: the API contract is camelCase, but `/auth/me` has
 * always emitted snake_case keys for this envelope). Both spellings are accepted so the client never silently
 * drops department access — dropping it reads as "no permissions at all" and strands the user on a blank route.
 */
type AuthMeRawDepartmentAccess = {
  departmentId?: string | null
  department_id?: string | null
  departmentName?: string | null
  department_name?: string | null
  departmentStatus?: string | null
  department_status?: string | null
  branchId?: string | null
  branch_id?: string | null
  branchName?: string | null
  branch_name?: string | null
  branchSlug?: string | null
  branch_slug?: string | null
  roleKeys?: string[] | null
  role_keys?: string[] | null
  permissions?: string[] | null
}

type AuthMeRawDepartmentEntry = {
  mode?: string | null
  landingDepartmentId?: string | null
  landing_department_id?: string | null
}

type AuthMeRawResponse = {
  auth?: {
    clerkUserId?: string | null
    clerk_user_id?: string | null
    localUserId?: string | null
    local_user_id?: string | null
    email?: string | null
    sessionId?: string | null
    session_id?: string | null
    isAuthenticated?: boolean | null
    is_authenticated?: boolean | null
  }
  authorizationRevision?: string | null
  authorization_revision?: string | null
  user?: {
    id?: string | null
    email?: string | null
    defaultWorkspaceId?: string | null
    default_workspace_id?: string | null
    clerkUserId?: string | null
    clerk_user_id?: string | null
    emailVerifiedAt?: string | null
    email_verified_at?: string | null
    lastLoginAt?: string | null
    last_login_at?: string | null
    firstName?: string | null
    first_name?: string | null
    lastName?: string | null
    last_name?: string | null
    phone?: string | null
    avatarUrl?: string | null
    avatar_url?: string | null
    status?: AuthMeResponse["user"]["status"] | null
    createdAt?: string | null
    created_at?: string | null
    updatedAt?: string | null
    updated_at?: string | null
  }
  pendingInvitations?: Array<{
    id?: string | null
    code?: string | null
    email?: string | null
    workspaceId?: string | null
    isSystemInvite?: boolean | null
    roleKey?: string | null
      status?: AuthMeResponse["pendingInvitations"][number]["status"] | null
      expiresAt?: string | null
      expires_at?: string | null
      viewedAt?: string | null
      viewed_at?: string | null
      createdAt?: string | null
      created_at?: string | null
    workspace?: {
      id?: string | null
      name?: string | null
      displayName?: string | null
      display_name?: string | null
      slug?: string | null
      logoUrl?: string | null
      logo_url?: string | null
      status?: string | null
      structureType?: string | null
      structure_type?: string | null
      systemBranch?: { id?: string | null; name?: string | null; slug?: string | null; timezone?: string | null } | null
      system_branch?: { id?: string | null; name?: string | null; slug?: string | null; timezone?: string | null } | null
      operationalBranch?: { id?: string | null; name?: string | null; slug?: string | null; timezone?: string | null } | null
      operational_branch?: { id?: string | null; name?: string | null; slug?: string | null; timezone?: string | null } | null
    } | null
  }>
  pending_invitations?: AuthMeRawResponse["pendingInvitations"]
  expiredInvitations?: Array<{
    id?: string | null
    code?: string | null
    email?: string | null
    workspaceId?: string | null
    isSystemInvite?: boolean | null
    roleKey?: string | null
    status?: AuthMeResponse["expiredInvitations"][number]["status"] | null
    expiresAt?: string | null
    expires_at?: string | null
    viewedAt?: string | null
    viewed_at?: string | null
    createdAt?: string | null
    created_at?: string | null
    workspace?: {
      id?: string | null
      name?: string | null
      displayName?: string | null
      display_name?: string | null
      slug?: string | null
      logoUrl?: string | null
      logo_url?: string | null
      status?: string | null
      structureType?: string | null
      structure_type?: string | null
    } | null
  }>
  expired_invitations?: AuthMeRawResponse["expiredInvitations"]
  workspaceAccess?: Array<{
    id?: string | null
    workspace?: {
      id?: string | null
      name?: string | null
      displayName?: string | null
      display_name?: string | null
      slug?: string | null
      description?: string | null
      description_text?: string | null
      logoUrl?: string | null
      logo_url?: string | null
      structureType?: string | null
      structure_type?: string | null
      /** The parish branch, sent only for a caller holding ORGANIZATION scope in a multi-branch workspace. */
      systemBranch?: { id?: string | null; name?: string | null; slug?: string | null } | null
      system_branch?: { id?: string | null; name?: string | null; slug?: string | null } | null
      operationalBranch?: { id?: string | null; name?: string | null; slug?: string | null } | null
      operational_branch?: { id?: string | null; name?: string | null; slug?: string | null } | null
      timezone?: string | null
      email?: string | null
      phone?: string | null
      defaultLanguage?: string | null
      default_language?: string | null
      status?: "PENDING" | "ACTIVE" | "INACTIVE" | "SUSPENDED" | null
      createdAt?: string | null
      created_at?: string | null
      updatedAt?: string | null
      updated_at?: string | null
    } | null
    member?: {
      id?: string | null
      workspaceId?: string | null
      workspace_id?: string | null
      branchId?: string | null
      branch_id?: string | null
      firstName?: string | null
      first_name?: string | null
      lastName?: string | null
      last_name?: string | null
      email?: string | null
      phone?: string | null
      avatarUrl?: string | null
      avatar_url?: string | null
      branch?: {
        id?: string | null
        workspaceId?: string | null
        workspace_id?: string | null
        name?: string | null
        slug?: string | null
        status?: string | null
        branchType?: "NORMAL" | "SYSTEM" | null
        branch_type?: "NORMAL" | "SYSTEM" | null
        baseCurrency?: string | null
        base_currency?: string | null
        timezone?: string | null
      } | null
    } | null
    workspaceRole?: "OWNER" | "ADMIN" | "USER" | null
    workspace_role?: "OWNER" | "ADMIN" | "USER" | null
    userBranchAccesses?: Array<{
      id?: string | null
      workspaceUserId?: string | null
      workspace_user_id?: string | null
      branchId?: string | null
      branch_id?: string | null
      status?: UserBranchAccessStatus | null
      branch?: {
        id?: string | null
        workspaceId?: string | null
        workspace_id?: string | null
        name?: string | null
        slug?: string | null
        status?: string | null
        branchType?: "NORMAL" | "SYSTEM" | null
        branch_type?: "NORMAL" | "SYSTEM" | null
        baseCurrency?: string | null
        base_currency?: string | null
        timezone?: string | null
      } | null
      createdAt?: string | null
      created_at?: string | null
      updatedAt?: string | null
      updated_at?: string | null
    }>
    user_branch_accesses?: NonNullable<AuthMeRawResponse["workspaceAccess"]>[number]["userBranchAccesses"]
    status?: "PENDING" | "ACTIVE" | "SUSPENDED" | null
    roles?: Array<{
      id?: string | null
      roleKey?: string | null
      role_key?: string | null
      roleName?: string | null
      role_name?: string | null
      roleScope?: "WORKSPACE" | "BRANCH" | null
      role_scope?: "WORKSPACE" | "BRANCH" | null
      roleStatus?: "ACTIVE" | "INACTIVE" | null
      role_status?: "ACTIVE" | "INACTIVE" | null
      branchId?: string | null
      branch_id?: string | null
      branch?: {
        id?: string | null
        slug?: string | null
        name?: string | null
        branchType?: "NORMAL" | "SYSTEM" | null
        branch_type?: "NORMAL" | "SYSTEM" | null
        baseCurrency?: string | null
        base_currency?: string | null
      } | null
      permissions?: string[] | null
    }>
    departmentAccess?: Array<AuthMeRawDepartmentAccess> | null
    department_access?: Array<AuthMeRawDepartmentAccess> | null
    departmentEntry?: AuthMeRawDepartmentEntry | null
    department_entry?: AuthMeRawDepartmentEntry | null
    createdAt?: string | null
    created_at?: string | null
    updatedAt?: string | null
    updated_at?: string | null
  }>
  workspace_access?: AuthMeRawResponse["workspaceAccess"]
  state?: string | null
  workspace_state?: string | null
}

type TenantBootstrapRawResponse = {
  auth?: AuthMeRawResponse["auth"]
  user?: AuthMeRawResponse["user"]
  authorizationRevision?: string | null
  authorization_revision?: string | null
  state?: string | null
  workspace_state?: string | null
  workspaceAccess?: AuthMeRawResponse["workspaceAccess"]
  workspace_access?: AuthMeRawResponse["workspaceAccess"]
  startup?: {
    redirect?: {
      kind?: TenantBootstrapResponse["startup"]["redirect"] extends infer T
        ? T extends { kind: infer K } ? K : never
        : never
      href?: string | null
    } | null
    activeWorkspaceCount?: number | null
    active_workspace_count?: number | null
    workspaceAccessHistoryCount?: number | null
    workspace_access_history_count?: number | null
    pendingInvitationCount?: number | null
    pending_invitation_count?: number | null
  } | null
  currentContext?: unknown
  current_context?: unknown
}

function toStringOrNull(value: string | null | undefined): string | null {
  return typeof value === "string" ? value : null
}

function toStringOrEmpty(value: string | null | undefined): string {
  return typeof value === "string" ? value : ""
}

function getPendingInvitationsRaw(input: AuthMeRawResponse) {
  return input.pendingInvitations ?? input.pending_invitations ?? []
}

function getExpiredInvitationsRaw(input: AuthMeRawResponse) {
  return input.expiredInvitations ?? input.expired_invitations ?? []
}

function getWorkspaceAccessRaw(input: AuthMeRawResponse) {
  return input.workspaceAccess ?? input.workspace_access ?? []
}

function assertAuthMePayload(input: AuthMeRawResponse): void {
  if (!Array.isArray(getPendingInvitationsRaw(input))) {
    throw new Error("Invalid auth payload: expected pending invitations array.")
  }

  if (!Array.isArray(getWorkspaceAccessRaw(input))) {
    throw new Error("Invalid auth payload: expected workspace access array.")
  }

  if (!Array.isArray(getExpiredInvitationsRaw(input))) {
    throw new Error("Invalid auth payload: expected expired invitations array.")
  }
}

function normalizeAuthMeWorkspaceState(value: unknown): AuthMeWorkspaceState | null {
  if (value === AuthMeWorkspaceState.NO_WORKSPACE_ACCESS) return AuthMeWorkspaceState.NO_WORKSPACE_ACCESS
  const normalized =
    typeof value === "string" ? value.toUpperCase().replace(/-/g, "_") : ""
  if (normalized === AuthMeWorkspaceState.NO_WORKSPACE_ACCESS) {
    return AuthMeWorkspaceState.NO_WORKSPACE_ACCESS
  }
  return null
}

function normalizeWorkspaceStructureType(value: unknown): WorkspaceStructureType {
  const raw = typeof value === "string" ? value.trim().toUpperCase() : ""
  if (raw === WorkspaceStructureType.SingleBranch) return WorkspaceStructureType.SingleBranch
  if (raw === WorkspaceStructureType.MultiBranch) return WorkspaceStructureType.MultiBranch
  return WorkspaceStructureType.MultiBranch
}

/**
 * The parish branch a caller reaches through ORGANIZATION scope, as sent by the server.
 *
 * Present only for a multi-branch workspace and a caller holding organization scope — absent means the
 * parish is reachable through an explicit branch grant or not at all, which is a different fact from "there
 * is no parish", so it stays null rather than becoming an empty object.
 */
function normalizeSystemBranch(
  branch: { id?: string | null; name?: string | null; slug?: string | null; timezone?: string | null } | null | undefined,
) {
  if (!branch?.id) return null
  return {
    id: toStringOrEmpty(branch.id),
    name: toStringOrEmpty(branch.name),
    slug: toStringOrEmpty(branch.slug),
    timezone: toStringOrEmpty(branch.timezone) || undefined,
  }
}

function normalizeWorkspaceSummary(
  workspace: NonNullable<NonNullable<AuthMeRawResponse["pendingInvitations"]>[number]["workspace"]> | null | undefined,
) {
  if (!workspace) return null

  return {
    id: toStringOrEmpty(workspace.id),
    name: toStringOrEmpty(workspace.name),
    displayName: toStringOrNull(workspace.displayName ?? workspace.display_name),
    slug: toStringOrEmpty(workspace.slug),
    logoUrl: toStringOrNull(workspace.logoUrl ?? workspace.logo_url),
    status: toStringOrEmpty(workspace.status),
    structureType: normalizeWorkspaceStructureType(
      workspace.structureType ?? workspace.structure_type,
    ),
    createdAt: toStringOrNull(
      (workspace as { createdAt?: string | null; created_at?: string | null }).createdAt ??
        (workspace as { created_at?: string | null }).created_at,
    ),
  }
}

export function normalizeAuthMeResponse(input: AuthMeRawResponse): AuthMeResponse {
  assertAuthMePayload(input)
  const pendingInvitationsRaw = getPendingInvitationsRaw(input)
  const expiredInvitationsRaw = getExpiredInvitationsRaw(input)
  const workspaceAccessRaw = getWorkspaceAccessRaw(input)

  const pendingInvitations = pendingInvitationsRaw.map((invite) => ({
    id: toStringOrEmpty(invite.id),
    code: toStringOrEmpty(invite.code),
    email: toStringOrEmpty(invite.email),
    workspaceId: toStringOrNull(invite.workspaceId),
    isSystemInvite: Boolean(invite.isSystemInvite),
    roleKey: toStringOrNull(invite.roleKey),
    status: (invite.status ?? "pending") as AuthMeResponse["pendingInvitations"][number]["status"],
    expiresAt: toStringOrEmpty(invite.expiresAt ?? invite.expires_at),
    viewedAt: toStringOrNull(invite.viewedAt ?? invite.viewed_at),
    createdAt: toStringOrEmpty(invite.createdAt ?? invite.created_at),
    workspace: normalizeWorkspaceSummary(invite.workspace),
  }))

  const workspaceAccess = workspaceAccessRaw.map((access) => ({
    id: toStringOrEmpty(access.id),
    workspace: {
      id: toStringOrEmpty(access.workspace?.id),
      name: toStringOrEmpty(access.workspace?.name),
      displayName: toStringOrNull(access.workspace?.displayName ?? access.workspace?.display_name),
      slug: toStringOrEmpty(access.workspace?.slug),
      description: toStringOrNull(access.workspace?.description ?? access.workspace?.description_text),
      logoUrl: toStringOrNull(access.workspace?.logoUrl ?? access.workspace?.logo_url),
      email: toStringOrNull(access.workspace?.email),
      phone: toStringOrNull(access.workspace?.phone),
      defaultLanguage: toStringOrEmpty(access.workspace?.defaultLanguage ?? access.workspace?.default_language),
      status: (access.workspace?.status ?? "ACTIVE") as NonNullable<AuthMeResponse["workspaceAccess"][number]["workspace"]>["status"],
      structureType: normalizeWorkspaceStructureType(
        access.workspace?.structureType ?? access.workspace?.structure_type,
      ),
      timezone: toStringOrEmpty(access.workspace?.timezone) || "Africa/Kigali",
      /*
       * The parish, for a user who reaches it through an ORGANIZATION role rather than a branch grant.
       *
       * This normalizer rebuilds the workspace field by field, and every field it forgets is silently gone —
       * the same footgun as `permissions` two blocks down. The server sends `systemBranch` precisely so a
       * user holding organization scope has a door into the scope they govern; dropped here, they had every
       * organization role and no "Organization" entry in the switcher, which is exactly what it looks like
       * when the backend has denied you something.
       */
      systemBranch: normalizeSystemBranch(access.workspace?.systemBranch ?? access.workspace?.system_branch),
      operationalBranch: normalizeSystemBranch(access.workspace?.operationalBranch ?? access.workspace?.operational_branch),
      createdAt: toStringOrEmpty(access.workspace?.createdAt ?? access.workspace?.created_at),
      updatedAt: toStringOrEmpty(access.workspace?.updatedAt ?? access.workspace?.updated_at),
    },
    member: access.member
      ? {
          id: toStringOrEmpty(access.member.id),
          workspaceId: toStringOrEmpty(access.member.workspaceId ?? access.member.workspace_id),
          branchId: toStringOrEmpty(access.member.branchId ?? access.member.branch_id),
          firstName: toStringOrEmpty(access.member.firstName ?? access.member.first_name),
          lastName: toStringOrEmpty(access.member.lastName ?? access.member.last_name),
          email: toStringOrNull(access.member.email),
          phone: toStringOrNull(access.member.phone),
          avatarUrl: toStringOrNull(access.member.avatarUrl ?? access.member.avatar_url),
          branch: access.member.branch
            ? {
                id: toStringOrEmpty(access.member.branch.id),
                workspaceId: toStringOrEmpty(access.member.branch.workspaceId ?? access.member.branch.workspace_id),
                name: toStringOrEmpty(access.member.branch.name),
                slug: toStringOrEmpty(access.member.branch.slug),
                status: toStringOrEmpty(access.member.branch.status),
                ...(access.member.branch.branchType ?? access.member.branch.branch_type
                  ? {
                      branchType: (access.member.branch.branchType ?? access.member.branch.branch_type) as
                        | "NORMAL"
                        | "SYSTEM",
                    }
                  : {}),
              }
            : null,
        }
      : null,
    workspaceRole: (access.workspaceRole ?? access.workspace_role) as AuthMeResponse["workspaceAccess"][number]["workspaceRole"],
    userBranchAccesses: ((access.userBranchAccesses ?? access.user_branch_accesses) ?? []).flatMap((entry) => {
      const branchId = toStringOrNull(entry.branchId ?? entry.branch_id)
      if (!entry?.id || !branchId) return []

      return [{
        id: toStringOrEmpty(entry.id),
        workspaceUserId: toStringOrNull(entry.workspaceUserId ?? entry.workspace_user_id),
        branchId,
        status: (entry.status ?? UserBranchAccessStatus.ACTIVE) as UserBranchAccessStatus,
        branch: entry.branch
          ? {
              id: toStringOrEmpty(entry.branch.id),
              workspaceId: toStringOrEmpty(entry.branch.workspaceId ?? entry.branch.workspace_id),
              name: toStringOrEmpty(entry.branch.name),
              slug: toStringOrEmpty(entry.branch.slug),
              status: toStringOrEmpty(entry.branch.status),
              ...(entry.branch.branchType ?? entry.branch.branch_type
                ? {
                    branchType: (entry.branch.branchType ?? entry.branch.branch_type) as "NORMAL" | "SYSTEM",
                  }
                : {}),
              baseCurrency: (entry.branch.baseCurrency ?? entry.branch.base_currency) as Currency | undefined,
              timezone: toStringOrEmpty(entry.branch.timezone) || undefined,
            }
          : null,
        createdAt: toStringOrNull(entry.createdAt ?? entry.created_at),
        updatedAt: toStringOrNull(entry.updatedAt ?? entry.updated_at),
      }]
    }),
    status: (access.status ?? "PENDING") as AuthMeResponse["workspaceAccess"][number]["status"],
    roles: (access.roles ?? []).map((role) => ({
      id: toStringOrEmpty(role.id),
      roleKey: toStringOrEmpty(role.roleKey ?? role.role_key),
      roleName: toStringOrEmpty(role.roleName ?? role.role_name),
      roleScope: (role.roleScope ?? role.role_scope ?? "WORKSPACE") as "WORKSPACE" | "BRANCH",
      roleStatus: role.roleStatus ?? role.role_status ?? undefined,
      branchId: toStringOrNull(role.branchId ?? role.branch_id),
      branch: role.branch
        ? {
          id: toStringOrNull(role.branch.id),
          slug: toStringOrNull(role.branch.slug),
          name: toStringOrNull(role.branch.name),
          ...(role.branch.branchType ?? role.branch.branch_type
            ? {
                branchType: (role.branch.branchType ?? role.branch.branch_type) as "NORMAL" | "SYSTEM",
              }
            : {}),
        }
        : null,
      // The permission strings ARE the grant — dropping them here left every custom role powerless, so a user
      // with a valid role (e.g. Read Only) could not access any module despite the backend granting it.
      permissions: (role.permissions ?? []).filter((permission): permission is string => typeof permission === "string"),
    })),
    departmentAccess: (access.departmentAccess ?? access.department_access ?? []).map((department) => ({
      departmentId: toStringOrEmpty(department.departmentId ?? department.department_id),
      departmentName: toStringOrEmpty(department.departmentName ?? department.department_name),
      departmentStatus: toStringOrEmpty(department.departmentStatus ?? department.department_status),
      branchId: toStringOrEmpty(department.branchId ?? department.branch_id),
      branchName: toStringOrEmpty(department.branchName ?? department.branch_name),
      branchSlug: toStringOrEmpty(department.branchSlug ?? department.branch_slug),
      roleKeys: (department.roleKeys ?? department.role_keys ?? []).filter((roleKey): roleKey is string => typeof roleKey === "string"),
      permissions: (department.permissions ?? []).filter((permission): permission is string => typeof permission === "string"),
    })),
    departmentEntry: (() => {
      const entry = access.departmentEntry ?? access.department_entry
      return {
        mode: entry?.mode === DepartmentEntryMode.Focused
          ? DepartmentEntryMode.Focused
          : entry?.mode === DepartmentEntryMode.Mixed
            ? DepartmentEntryMode.Mixed
            : DepartmentEntryMode.None,
        landingDepartmentId: toStringOrNull(entry?.landingDepartmentId ?? entry?.landing_department_id),
      }
    })(),
    createdAt: toStringOrEmpty(access.createdAt ?? access.created_at),
    updatedAt: toStringOrEmpty(access.updatedAt ?? access.updated_at),
  }))

  return {
    auth: {
      clerkUserId: toStringOrEmpty(input.auth?.clerkUserId ?? input.auth?.clerk_user_id),
      localUserId: toStringOrEmpty(input.auth?.localUserId ?? input.auth?.local_user_id),
      email: toStringOrEmpty(input.auth?.email),
      sessionId: toStringOrNull(input.auth?.sessionId ?? input.auth?.session_id),
      isAuthenticated: Boolean(input.auth?.isAuthenticated ?? input.auth?.is_authenticated) as true,
    },
    authorizationRevision: toStringOrEmpty(input.authorizationRevision ?? input.authorization_revision),
    user: {
      id: toStringOrEmpty(input.user?.id),
      email: toStringOrEmpty(input.user?.email),
      defaultWorkspaceId: toStringOrNull(input.user?.defaultWorkspaceId ?? input.user?.default_workspace_id),
      clerkUserId: toStringOrNull(input.user?.clerkUserId ?? input.user?.clerk_user_id),
      emailVerifiedAt: toStringOrNull(input.user?.emailVerifiedAt ?? input.user?.email_verified_at),
      lastLoginAt: toStringOrNull(input.user?.lastLoginAt ?? input.user?.last_login_at),
      firstName: toStringOrEmpty(input.user?.firstName ?? input.user?.first_name),
      lastName: toStringOrEmpty(input.user?.lastName ?? input.user?.last_name),
      phone: toStringOrNull(input.user?.phone),
      avatarUrl: toStringOrNull(input.user?.avatarUrl ?? input.user?.avatar_url),
      status: (input.user?.status ?? "PENDING") as AuthMeResponse["user"]["status"],
      createdAt: toStringOrEmpty(input.user?.createdAt ?? input.user?.created_at),
      updatedAt: toStringOrEmpty(input.user?.updatedAt ?? input.user?.updated_at),
    },
    pendingInvitations,
    expiredInvitations: expiredInvitationsRaw.map((invite) => ({
      id: toStringOrEmpty(invite.id),
      code: toStringOrEmpty(invite.code),
      email: toStringOrEmpty(invite.email),
      workspaceId: toStringOrNull(invite.workspaceId),
      isSystemInvite: Boolean(invite.isSystemInvite),
      roleKey: toStringOrNull(invite.roleKey),
      status: (invite.status ?? "expired") as AuthMeResponse["expiredInvitations"][number]["status"],
      expiresAt: toStringOrEmpty(invite.expiresAt ?? invite.expires_at),
      viewedAt: toStringOrNull(invite.viewedAt ?? invite.viewed_at),
      createdAt: toStringOrEmpty(invite.createdAt ?? invite.created_at),
      workspace: normalizeWorkspaceSummary(invite.workspace),
    })),
    state: normalizeAuthMeWorkspaceState(input.state ?? input.workspace_state),
    workspaceAccess,
  }
}

function createAuthMeShellFromRaw(
  input: Pick<
    AuthMeRawResponse,
    | "auth"
    | "user"
    | "authorizationRevision"
    | "authorization_revision"
    | "workspaceAccess"
    | "workspace_access"
    | "state"
    | "workspace_state"
  >,
): AuthMeResponse {
  return normalizeAuthMeResponse({
    ...input,
    pendingInvitations: [],
    expiredInvitations: [],
  } as AuthMeRawResponse)
}

function normalizeTenantAccessResolveResponse(input: Record<string, unknown>): TenantAccessResolveResponse {
  const membership = typeof input.membership === "object" && input.membership !== null
    ? input.membership as Record<string, unknown>
    : {}
  const branchContextRaw =
    typeof input.branchContext === "object" && input.branchContext !== null
      ? input.branchContext as Record<string, unknown>
      : typeof input.branch_context === "object" && input.branch_context !== null
        ? input.branch_context as Record<string, unknown>
        : {}
  const accessibleBranchesRaw = branchContextRaw.accessibleBranches ?? branchContextRaw.accessible_branches
  const suggestions =
    typeof input.suggestions === "object" && input.suggestions !== null
      ? input.suggestions as Record<string, unknown>
      : {}
  const defaults =
    typeof input.defaults === "object" && input.defaults !== null
      ? input.defaults as Record<string, unknown>
      : {}
  const access =
    typeof input.access === "object" && input.access !== null
      ? input.access as Record<string, unknown>
      : {}
  const request =
    typeof input.request === "object" && input.request !== null
      ? input.request as Record<string, unknown>
      : {}
  const resolved =
    typeof input.resolved === "object" && input.resolved !== null
      ? input.resolved as Record<string, unknown>
      : {}

  return {
    selectionSource: toStringOrEmpty(
      typeof input.selectionSource === "string"
        ? input.selectionSource
        : typeof input.selection_source === "string"
          ? input.selection_source
          : "NONE",
    ) as TenantAccessResolveResponse["selectionSource"],
    canonicalLanding: toStringOrNull(
      typeof input.canonicalLanding === "string"
        ? input.canonicalLanding
        : typeof input.canonical_landing === "string"
          ? input.canonical_landing
          : null,
    ),
    invalidRequestReason: (
      typeof input.invalidRequestReason === "string"
        ? input.invalidRequestReason
        : typeof input.invalid_request_reason === "string"
          ? input.invalid_request_reason
          : null
    ) as TenantAccessResolveResponse["invalidRequestReason"],
    recoveryLanding: toStringOrNull(
      typeof input.recoveryLanding === "string"
        ? input.recoveryLanding
        : typeof input.recovery_landing === "string"
          ? input.recovery_landing
          : null,
    ),
    request: {
      workspaceSlug: toStringOrNull(
        typeof request.workspaceSlug === "string"
          ? request.workspaceSlug
          : typeof request.workspace_slug === "string"
            ? request.workspace_slug
            : null,
      ),
      branchSlug: toStringOrNull(
        typeof request.branchSlug === "string"
          ? request.branchSlug
          : typeof request.branch_slug === "string"
            ? request.branch_slug
            : null,
      ),
    },
    resolved: {
      workspace: normalizeWorkspaceSummary(
        (resolved.workspace ?? null) as NonNullable<
          NonNullable<AuthMeRawResponse["pendingInvitations"]>[number]["workspace"]
        > | null,
      ),
      branch: ((): TenantAccessResolveResponse["resolved"]["branch"] => {
        const branch = resolved.branch
        if (!branch || typeof branch !== "object") return null
        const row = branch as Record<string, unknown>
        return {
          id: toStringOrEmpty(typeof row.id === "string" ? row.id : null),
          workspaceId: toStringOrEmpty(
            typeof row.workspaceId === "string"
              ? row.workspaceId
              : typeof row.workspace_id === "string"
                ? row.workspace_id
                : null,
          ),
          name: toStringOrEmpty(typeof row.name === "string" ? row.name : null),
          slug: toStringOrEmpty(typeof row.slug === "string" ? row.slug : null),
          status: toStringOrEmpty(typeof row.status === "string" ? row.status : null),
          branchType: toStringOrNull(
            typeof row.branchType === "string"
              ? row.branchType
              : typeof row.branch_type === "string"
                ? row.branch_type
                : null,
          ) as NonNullable<TenantAccessResolveResponse["resolved"]["branch"]>["branchType"],
        }
      })(),
    },
    defaults: {
      workspaceApplied: Boolean(defaults.workspaceApplied ?? defaults.workspace_applied),
      branchApplied: Boolean(defaults.branchApplied ?? defaults.branch_applied),
    },
    access: {
      allowed: Boolean(access.allowed),
      hasWorkspaceAccess: Boolean(access.hasWorkspaceAccess ?? access.has_workspace_access),
      hasBranchAccess: Boolean(access.hasBranchAccess ?? access.has_branch_access),
      reason: (typeof access.reason === "string" ? access.reason : null) as TenantAccessResolveResponse["access"]["reason"],
    },
    suggestions: {
      defaultWorkspaceSlug: toStringOrNull(
        typeof suggestions.defaultWorkspaceSlug === "string"
          ? suggestions.defaultWorkspaceSlug
          : typeof suggestions.default_workspace_slug === "string"
            ? suggestions.default_workspace_slug
            : null,
      ),
      defaultBranchSlug: toStringOrNull(
        typeof suggestions.defaultBranchSlug === "string"
          ? suggestions.defaultBranchSlug
          : typeof suggestions.default_branch_slug === "string"
            ? suggestions.default_branch_slug
            : null,
      ),
    },
    membership: {
      workspaceUserId: toStringOrNull(
        typeof membership.workspaceUserId === "string"
          ? membership.workspaceUserId
          : typeof membership.workspace_user_id === "string"
            ? membership.workspace_user_id
            : null,
      ),
      status: toStringOrNull(typeof membership.status === "string" ? membership.status : null),
      roles: Array.isArray(membership.roles)
        ? membership.roles.flatMap((role) => {
            if (!role || typeof role !== "object") return []
            const row = role as Record<string, unknown>
            return [{
              roleKey: toStringOrEmpty(
                typeof row.roleKey === "string"
                  ? row.roleKey
                  : typeof row.role_key === "string"
                    ? row.role_key
                    : null,
              ),
              roleName: toStringOrEmpty(
                typeof row.roleName === "string"
                  ? row.roleName
                  : typeof row.role_name === "string"
                    ? row.role_name
                    : null,
              ),
              roleScope: (
                typeof row.roleScope === "string"
                  ? row.roleScope
                  : typeof row.role_scope === "string"
                    ? row.role_scope
                    : "WORKSPACE"
              ) as TenantAccessResolveResponse["membership"]["roles"][number]["roleScope"],
              branchId: toStringOrNull(
                typeof row.branchId === "string"
                  ? row.branchId
                  : typeof row.branch_id === "string"
                    ? row.branch_id
                    : null,
              ),
              branchSlug: toStringOrNull(
                typeof row.branchSlug === "string"
                  ? row.branchSlug
                  : typeof row.branch_slug === "string"
                    ? row.branch_slug
                    : null,
              ),
            }]
          })
        : [],
    },
    branchContext: {
      accessibleBranches: Array.isArray(accessibleBranchesRaw)
        ? accessibleBranchesRaw.flatMap((branch: unknown) => {
            if (!branch || typeof branch !== "object") return []
            const row = branch as Record<string, unknown>
            return [{
              id: toStringOrEmpty(typeof row.id === "string" ? row.id : null),
              workspaceId: toStringOrEmpty(
                typeof row.workspaceId === "string"
                  ? row.workspaceId
                  : typeof row.workspace_id === "string"
                    ? row.workspace_id
                    : null,
              ),
              name: toStringOrEmpty(typeof row.name === "string" ? row.name : null),
              slug: toStringOrEmpty(typeof row.slug === "string" ? row.slug : null),
              status: toStringOrEmpty(typeof row.status === "string" ? row.status : null),
              branchType: toStringOrNull(
                typeof row.branchType === "string"
                  ? row.branchType
                  : typeof row.branch_type === "string"
                    ? row.branch_type
                    : null,
              ),
              // IBA-114 — the branch's accounting currency travels with the branch summary.
              baseCurrency: toStringOrNull(
                typeof row.baseCurrency === "string"
                  ? row.baseCurrency
                  : typeof row.base_currency === "string"
                    ? row.base_currency
                    : null,
              ),
            }]
          })
        : [],
      hasNormalBranchAccess: Boolean(
        branchContextRaw.hasNormalBranchAccess ?? branchContextRaw.has_normal_branch_access,
      ),
      hasSystemBranchAccess: Boolean(
        branchContextRaw.hasSystemBranchAccess ?? branchContextRaw.has_system_branch_access,
      ),
      requiresBranchSelection: Boolean(
        branchContextRaw.requiresBranchSelection ?? branchContextRaw.requires_branch_selection,
      ),
      requiresWorkspaceSetup: Boolean(
        branchContextRaw.requiresWorkspaceSetup ?? branchContextRaw.requires_workspace_setup,
      ),
    },
  }
}

function normalizeTenantBootstrapResponse(input: TenantBootstrapRawResponse): TenantBootstrapResponse {
  const authShell = createAuthMeShellFromRaw({
    auth: input.auth,
    user: input.user,
    authorizationRevision: input.authorizationRevision,
    authorization_revision: input.authorization_revision,
    workspaceAccess: input.workspaceAccess,
    workspace_access: input.workspace_access,
    state: input.state,
    workspace_state: input.workspace_state,
  })

  const redirectRaw = input.startup?.redirect
  const redirect =
    redirectRaw && typeof redirectRaw.kind === "string" && typeof redirectRaw.href === "string" && redirectRaw.href.length > 0
      ? {
          kind: redirectRaw.kind,
          href: redirectRaw.href,
        }
      : null

  const currentContext = normalizeTenantAccessResolveResponse(
    (input.currentContext ?? input.current_context ?? {}) as Record<string, unknown>,
  )

  return {
    auth: authShell.auth,
    user: authShell.user,
    authorizationRevision: authShell.authorizationRevision,
    state: authShell.state,
    workspaceAccess: authShell.workspaceAccess,
    startup: {
      redirect,
      activeWorkspaceCount:
        typeof input.startup?.activeWorkspaceCount === "number"
          ? input.startup.activeWorkspaceCount
          : typeof input.startup?.active_workspace_count === "number"
            ? input.startup.active_workspace_count
            : authShell.workspaceAccess.length,
      workspaceAccessHistoryCount:
        typeof input.startup?.workspaceAccessHistoryCount === "number"
          ? input.startup.workspaceAccessHistoryCount
          : typeof input.startup?.workspace_access_history_count === "number"
            ? input.startup.workspace_access_history_count
            : authShell.workspaceAccess.length,
      pendingInvitationCount:
        typeof input.startup?.pendingInvitationCount === "number"
          ? input.startup.pendingInvitationCount
          : typeof input.startup?.pending_invitation_count === "number"
            ? input.startup.pending_invitation_count
            : 0,
    },
    currentContext,
  }
}

type AcceptWorkspaceInvitationResponseRaw = Omit<AcceptWorkspaceInvitationResponse, "authContext"> & {
  authContext: AuthMeRawResponse | null
}

type WorkspaceBySlugRaw = {
  id?: string | null
  name?: string | null
  displayName?: string | null
  display_name?: string | null
  slug?: string | null
  logoUrl?: string | null
  logo_url?: string | null
  status?: string | null
  structureType?: string | null
  structure_type?: string | null
}

function normalizeWorkspaceLookupResponse(workspace: WorkspaceBySlugRaw): WorkspaceSummary {
  return {
    id: toStringOrEmpty(workspace.id),
    name: toStringOrEmpty(workspace.name),
    displayName: toStringOrNull(workspace.displayName ?? workspace.display_name),
    slug: toStringOrEmpty(workspace.slug),
    logoUrl: toStringOrNull(workspace.logoUrl ?? workspace.logo_url),
    status: toStringOrEmpty(workspace.status),
    structureType: normalizeWorkspaceStructureType(
      workspace.structureType ?? workspace.structure_type,
    ),
    createdAt: toStringOrNull(
      (workspace as { createdAt?: string | null; created_at?: string | null }).createdAt ??
        (workspace as { created_at?: string | null }).created_at,
    ),
  }
}

export async function getWorkspaceBySlug(slug: string) {
  const response = await http.get<WorkspaceBySlugRaw>(`/admin/workspaces/slug/${encodeURIComponent(slug)}`)
  return normalizeWorkspaceLookupResponse(response.data)
}

export async function resolveTenantAccess(input: { workspaceSlug?: string; branchSlug?: string }) {
  const response = await http.post<Record<string, unknown>>("/tenant/auth/tenant-access/resolve", {
    workspaceSlug: input.workspaceSlug ?? "",
    branchSlug: input.branchSlug ?? "",
  })
  return normalizeTenantAccessResolveResponse(response.data)
}

export async function getTenantBootstrap(token?: string | null) {
  const response = await http.get<TenantBootstrapRawResponse>("/tenant/auth/bootstrap", {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
  return normalizeTenantBootstrapResponse(response.data)
}

export async function recordTenantContext(input: { workspaceId: string; branchId: string }) {
  await http.post("/tenant/auth/last-context", input)
}

/**
 * Saves the user's UI language (ADR-0030). The API falls back to it when a request carries no `X-Locale`, and
 * uses it for emails and exports produced outside a request.
 */
export async function saveUserLanguage(language: AppLanguage) {
  await http.put("/tenant/auth/language", { language })
}

export async function getAuthMe(token?: string | null) {
  const response = await http.get<AuthMeRawResponse>("/tenant/auth/me", {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
  return normalizeAuthMeResponse(response.data)
}

export async function setDefaultWorkspace(input: SetDefaultWorkspaceRequest) {
  const response = await http.post<AuthMeRawResponse>("/tenant/auth/default-workspace", input)
  return normalizeAuthMeResponse(response.data)
}

export async function resolveInvitation(
  code: string,
  options: { token?: string | null; workspaceId?: string | null } = {}
) {
  const params = new URLSearchParams()
  if (options.workspaceId) {
    params.set("workspaceId", options.workspaceId)
  }
  const query = params.toString()
  const safeCode = encodeURIComponent(code)
  const path = query
    ? `/admin/invitations/resolve/${safeCode}?${query}`
    : `/admin/invitations/resolve/${safeCode}`
  const response = await http.get<ResolveWorkspaceInvitationResponse>(path, {
    headers: options.token ? { Authorization: `Bearer ${options.token}` } : undefined,
  })
  return response.data
}

export async function acceptInvitation(
  input: AcceptWorkspaceInvitationRequest,
  token?: string | null
) {
  const response = await http.post<AcceptWorkspaceInvitationResponseRaw>(
    "/admin/invitations/accept",
    input,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  )
  const normalized: AcceptWorkspaceInvitationResponse = {
    ...response.data,
    authContext: response.data.authContext ? normalizeAuthMeResponse(response.data.authContext) : null,
  }
  return normalized
}

export async function getInvitationPreview(invitationId: string) {
  const response = await http.get<InvitationPreviewResponse>(
    `/invitations/${encodeURIComponent(invitationId)}/preview`
  )
  return response.data
}

export async function acceptInvitationById(invitationId: string, token?: string | null) {
  const response = await http.post<AcceptWorkspaceInvitationResponseRaw>(
    `/invitations/${encodeURIComponent(invitationId)}/accept`,
    {},
    {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  )
  const normalized: AcceptWorkspaceInvitationResponse = {
    ...response.data,
    authContext: response.data.authContext ? normalizeAuthMeResponse(response.data.authContext) : null,
  }
  return normalized
}

export async function declineInvitation(
  input: DeclineWorkspaceInvitationRequest,
  token?: string | null
) {
  const response = await http.post<DeclineWorkspaceInvitationResponse>(
    "/admin/invitations/decline",
    input,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  )
  return response.data
}

export async function declineInvitationById(invitationId: string, token?: string | null) {
  const response = await http.post<DeclineWorkspaceInvitationResponse>(
    `/invitations/${encodeURIComponent(invitationId)}/decline`,
    {},
    {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  )
  return response.data
}

export async function getNewestUnviewedWorkspaceInvitation() {
  const response = await http.get<InvitationRecord | null>("/admin/invitations/unviewed")
  return response.data
}

export async function markWorkspaceInvitationsViewed(input: MarkWorkspaceInvitationsViewedRequest) {
  const response = await http.patch<MarkWorkspaceInvitationsViewedResponse>("/admin/invitations/viewed", input)
  return response.data
}

export async function createBranch(input: CreateBranchRequest, token?: string | null) {
  const response = await http.post<CreateBranchResponse>(
    "/branches",
    input,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  )
  return response.data
}
